/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import useEBTChargeAmountQuery from 'api/queries/useEBTChargeAmountQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import useForage from 'contexts/ForageContext/useForage';
import { LoadingIcon } from 'honeydew/Loader';
import DrawerModal from 'shared/DrawerModal';

import EBTPaymentFlowErrorBoundary, { ErrorComponent } from './ErrorBoundary';
import AddEBTCard from './steps/AddEBTCard';
import CheckBalance from './steps/CheckBalance';
import CreateEBTPayment from './steps/CreateEBTPayment';
import CreateEBTPaymentSuccess from './steps/CreateEBTPaymentSuccess';
import EditEBTPaymentAmount from './steps/EditEBTPaymentAmount';
import EditEBTPaymentConfirm from './steps/EditEBTPaymentConfirm';
import EditEBTPaymentSuccess from './steps/EditEBTPaymentSuccess';

const STEP_COMPONENTS = {
    AddEBTCard,
    CheckBalance,
    CreateEBTPayment,
    CreateEBTPaymentSuccess,
    EditEBTPaymentAmount,
    EditEBTPaymentConfirm,
    EditEBTPaymentSuccess,
};

/**
 * Orchestrates the EBT payment flow.
 *
 * @param {Object} cartData - Cart data containing SNAP-eligible totals and other cart information
 * @param {Function} onClose - Callback function to close the payment flow modal
 */
function EBTPaymentFlow({ cartData, onClose }) {
    const { clearForage, error: forageError, forage, initializeForage, isLoading: isForageLoading } = useForage();
    const { data: account } = useAccountQuery();
    const { data: subscription = {}, isPending: isSubscriptionPending } = useSubscriptionQuery();
    const { data: ebtChargeAmount, isPending: isEbtChargeAmountPending } = useEBTChargeAmountQuery();

    const initializing = useRef(false);
    const isFlowReady = forage && !forageError && !isEbtChargeAmountPending && !isSubscriptionPending;

    const [disableClose, setDisableClose] = useState(false);
    const [currentStep, setCurrentStep] = useState(null);
    const [currentStepData, setCurrentStepData] = useState(null);
    const [isStepLoading, setIsStepLoading] = useState(false);

    const currentAmount = ebtChargeAmount?.ebtAmount;
    const ebtCard = subscription?.ebtCard;

    const [flowData, setFlowData] = useState({
        newAmount: null,
        balance: null,
        apiResponse: null,
        previousStep: null,
    });

    // Set initial step when flow is ready
    useEffect(() => {
        if (!isFlowReady) return;

        if (!ebtCard?.last4) {
            setCurrentStep('AddEBTCard');
        } else if (currentAmount) {
            setCurrentStep('EditEBTPaymentAmount');
        } else {
            setCurrentStep('CreateEBTPayment');
        }
    }, [isFlowReady]);

    const initFlow = async () => {
        try {
            initializing.current = true;
            await initializeForage(account.customerID);
        } finally {
            initializing.current = false;
        }
    };

    // Init on mount and clear Forage when component unmounts
    useEffect(() => {
        if (!initializing.current && !forage) {
            initFlow();
        }

        return () => {
            // clear Forage when component unmounts to prevent session expiration
            clearForage?.();
        };
    }, []);

    const handleNext = useCallback(
        (nextStep, nextStepData = null) => {
            if (nextStep) {
                setFlowData((prev) => ({ ...prev, previousStep: currentStep }));
                setCurrentStep(nextStep);
                setCurrentStepData(nextStepData);
            } else {
                onClose();
            }
        },
        [onClose, currentStep]
    );

    const handleClose = useCallback(() => {
        setCurrentStep(null);
        onClose();
    }, [onClose]);

    const stepProps = useMemo(
        () => ({
            cartData,
            currentAmount,
            currentStepData,
            ebtCard,
            setDisableClose,
            setIsStepLoading,
            handleNext,
            handleClose,
            isStepLoading,
            flowData,
            setFlowData,
        }),
        [
            cartData,
            currentAmount,
            currentStepData,
            ebtCard,
            setDisableClose,
            setIsStepLoading,
            handleNext,
            handleClose,
            flowData,
            setFlowData,
            isStepLoading,
        ]
    );

    const CurrentStepComponent = STEP_COMPONENTS[currentStep];

    const renderContent = useMemo(() => {
        if (forageError) {
            return <ErrorComponent error={forageError} />;
        }

        if (isForageLoading || isEbtChargeAmountPending || !currentStep) {
            return (
                <div
                    className="flex h-[300px] items-center justify-center"
                    data-testid="ebt-payment-flow-not-ready-loader"
                >
                    <LoadingIcon />
                </div>
            );
        }

        if (!forage) {
            return <ErrorComponent error="Forage not initialized" />;
        }

        if (CurrentStepComponent) {
            return <CurrentStepComponent {...stepProps} />;
        }

        return <ErrorComponent error="Step component not found" />;
    }, [isForageLoading, isEbtChargeAmountPending, currentStep, forageError, CurrentStepComponent, stepProps]);

    return (
        <DrawerModal onClose={handleClose} disableClose={disableClose}>
            <EBTPaymentFlowErrorBoundary
                onClose={handleClose}
                currentStep={currentStep}
                onRetry={!forage ? initFlow : undefined}
            >
                {renderContent}
                <div
                    className={`absolute inset-0 z-50 flex items-center justify-center bg-white/80 transition-opacity duration-300 ${
                        isStepLoading ? 'opacity-100' : 'invisible opacity-0'
                    }`}
                >
                    <LoadingIcon />
                </div>
            </EBTPaymentFlowErrorBoundary>
        </DrawerModal>
    );
}

export default EBTPaymentFlow;
